import { useEffect, useState } from "react";

import {
  Autocomplete,
  Select,
  SelectData,
  Table,
  TableMetadata,
  TextField,
  Typography,
  useTable,
} from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models/Common/CodeBook";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { getActiveProgramTypes } from "../../api/ProgramType/programTypeApi";
import styles from "../../features/ProgramRequest/ProgramRequestDetails.module.scss";
import { CompetenceInfo } from "../../features/ProgramRequestEdit/CompetenceInfo";
import { ConditionalBasicInfo } from "../../features/ProgramRequestEdit/ConditionalBasicInfo";
import { OtherEnrollmentConditionsInfo } from "../../features/ProgramRequestEdit/OtherEnrollmentConditionsInfo";
import { CorrectionString } from "../../models/Correction";
import {
  ProgramType, ProgramTypeCodeBook,
} from "../../models/Program";
import { OtherEnrollmentConditionId, OtherFormOfTeachingId, OtherKnowledgeCheckId, ProgramRequest, ProgramRequestDetail } from "../../models/ProgramRequest";
import { Module } from "../../models/ProgramRequest/Module";
import {
  useGetEnrollmentConditionsQuery,
  useGetFormsOfTeachingQuery,
  useGetKnowledgeChecksQuery,
  useGetProgramsByTypeQuery
} from "../../queries/codeBookQuery";
import OtherData from "../OtherData";
import StaticField from "../StaticField/StaticField";
import { StringListDiffField } from "../StringListDiffField/StringListDiffField";

export interface ProgramRequestDetailPanelProps {
  name: string;
  programRequestDetail: ProgramRequestDetail;
  programRequestOriginal?: ProgramRequestDetail;
  programRequest: ProgramRequest;
  readonly?: boolean;
  onChange: (e: any) => void;
  onProgramRequestDetailChange: (detail: ProgramRequestDetail) => void;
  getErrorForPath: (path: string) => string | undefined;
  errors: any;
};

export const ProgramRequestDetailPanel = ({
  name,
  programRequestDetail,
  programRequestOriginal,
  programRequest,
  readonly = false,
  onChange,
  onProgramRequestDetailChange,
  getErrorForPath,
  errors,
}: ProgramRequestDetailPanelProps) => {
  const { t } = useTranslation();

  const [programRequestInternal, setProgramRequestInternal] = useState<ProgramRequest>(programRequest);
  const [programTypes, setProgramTypes] = useState<ProgramTypeCodeBook[]>([]);
  const [competencesDifferences, setCompetencesDifferences] = useState<CorrectionString[]>([]);
  const { data: programs } = useGetProgramsByTypeQuery(programRequest.programTypeId!, {
    skip: !programRequest.programTypeId,
  });
  const { data: formsOfTeaching } = useGetFormsOfTeachingQuery();
  const { data: knowledgeChecks } = useGetKnowledgeChecksQuery();
  const { data: enrollmentConditions } = useGetEnrollmentConditionsQuery();

  const programRequestTableMetadata: TableMetadata<CorrectionString> = {
    rowKey: "id",
    columns: [
      {
        columnKey: "programRequestCompetence",
        header: t("request.competenceName"),
        accessor: (item: CorrectionString) => {
          return (
            <div>
              <StaticField
                label={""}
                value={item.oldValue}
                newValue={!programRequestOriginal ? undefined : item.newValue}
                className={styles.changedCompetence}
              />
            </div>
          );
        }
      }
    ],
    rowToolTip: () => undefined
  };



  var [tableProps] = useTable<string>(
    {
      metadata: programRequestTableMetadata,
      data: competencesDifferences,
      pagination: false,
      totalItems: competencesDifferences.length,
    }
  );

  useEffect(() => {
    (async () => {
      if (programRequest.id === 0) {
        const response = await getActiveProgramTypes(undefined);
        setProgramTypes(response);
      }
    })();
  }, [programRequest.id]);

  useEffect(() => {

    var competencesDiff: CorrectionString[] = [];

    if (programRequestDetail && programRequestDetail.competences) {

      programRequestDetail.competences.map((competence) => {
        if (programRequestOriginal && programRequestOriginal.competences) {
          var competenceCorrection = programRequestOriginal.competences.find((corr) => corr.sourceCompetenceId === competence.id);

          if (competenceCorrection) {
            competencesDiff.push({
              id: competenceCorrection.id!,
              oldValue: competence.name,
              newValue: competenceCorrection.name
            });
            return;
          }
        }
        competencesDiff.push({
          id: competence.id!,
          oldValue: competence.name,
          newValue: "--"
        });
      });
    }

    if (programRequestOriginal && programRequestOriginal.competences) {
      programRequestOriginal.competences.map((correction) => {
        if (programRequestDetail && programRequestDetail.competences) {
          var competence = programRequestDetail.competences.find((comp) => comp.id === correction.sourceCompetenceId);

          if (competence) {
            //treba već bit obrađeno
            return;
          }

          competencesDiff.push({
            id: correction.id!,
            oldValue: "--",
            newValue: correction.name
          });

        }
      });
    }

    setCompetencesDifferences(competencesDiff);
  }, [programRequestDetail, programRequestOriginal]);

  const getProgramsSelectData = (): SelectData<CodeBook> => {
    if (programs == null) {
      return codeBookToSelectData(undefined);
    } else if (
      programRequest.programTypeId &&
      (
        programRequest.programTypeId === ProgramType.TrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
        programRequest.programTypeId === ProgramType.CraftsSpecializationPrograms ||
        programRequest.programTypeId === ProgramType.RefinementPrograms         
      )
    ) {
      return codeBookToSelectData([
        { id: -1, name: t("common.nothingOffered") },
        ...programs,
      ]);
    } else {
      return codeBookToSelectData(programs);
    }
  };
  const getOriginalLearningTotal = (): number | undefined => {
    let total = (Number(programRequestOriginal?.guidedLearningDuration) || 0) +
      (Number(programRequestOriginal?.independentLearningDuration) || 0) +
      (Number(programRequestOriginal?.workLearningDuration) || 0);
    return total === 0 ? undefined : total;
  }

  const getOriginalCSVETTotal = (): number | undefined => {
    let totalCSVET = programRequestOriginal?.modules?.reduce((a, v) => a = a + v.learningSets.reduce((a, v) => a = a + (isNaN(v.csvet) ? 0 : v.csvet), 0), 0);
    return totalCSVET === 0 ? undefined : totalCSVET;
  }

  const handleProgramTypeChange = (event: any) => {
    const programTypeId = event.target.value;
    let newModules = [] as Module[]
    if (programTypeId === ProgramType.TrainingPrograms ||
      programTypeId === ProgramType.CraftsAndArtsTrainingPrograms ||
      programTypeId === ProgramType.CraftsSpecializationPrograms ||
      programTypeId === ProgramType.RefinementPrograms ||      
      programTypeId === ProgramType.NewVocationalCurriculum) {
      newModules.push({ name: "", learningSets: [{ name: "", code: "", csvet: 1, level: 2 }] });
    };

    let newCompetences = [] as string[];
    newCompetences.push("");

    setProgramRequestInternal((prev) => ({
      ...prev,
      programId: undefined,
      qualificationTypeId: undefined,
      languageLevelBusinessIds: [],
      languageLevelGeneralIds: [],
      educationSectorId: undefined,
      educationSubSectorId: undefined,
      professionId: undefined,
      programNameFreeEntry: undefined,
      programTypeId: event.target.value,
      hkoLevelId: undefined,
      modules: newModules,
      competences: newCompetences
    }));
  };
  const handleGuidedLearningDuration = (event: any) => {
    const val = Number(event.target.value) || 0;
    const p: ProgramRequestDetail = {
      ...programRequestDetail,
      guidedLearningDuration: val,
    };
    doChange(event, p);
  };
  const handleWorkLearningDuration = (event: any) => {
    const val = Number(event.target.value) || 0;
    const p: ProgramRequestDetail = {
      ...programRequestDetail,
      workLearningDuration: val,
    };
    doChange(event, p);
  };
  const handleIndependentLearningDuration = (event: any) => {
    const val = Number(event.target.value) || 0;
    const p: ProgramRequestDetail = {
      ...programRequestDetail,
      independentLearningDuration: val,
    };
    doChange(event, p);
  };
  const handleProgramChange = (event: any) => {
    const programId = event.target.value;

    var program = programs?.find(pr => pr.id === programId);
    var details = programRequestDetail;

    if (program) {
      var hkoLevelId = program.hkoLevelId;
      var qualificationTypeId = program.qualificationTypeId;
      var educationSectorId = program.educationSectorId;
      var educationSubSectorId = program.educationSubSectorId;

      details = {
        ...programRequestDetail,
        programId: programId,
        hkoLevelId: hkoLevelId,
        qualificationTypeId: qualificationTypeId,
        educationSectorId: educationSectorId,
        educationSubSectorId: educationSubSectorId,
        programCode: program?.code,
      };
    } else {
      details = {
        ...programRequestDetail,
        programId: programId
      };
    }
    doChange(event, details);
  };
  const handleFormsOfTeachingChange = (event: any) => {
    const newFormsOfTeaching = event.target.value as number[];
    let details: ProgramRequestDetail;
    if (programRequestDetail.formOfTeachingIds.includes(OtherFormOfTeachingId) &&
      !newFormsOfTeaching.includes(OtherFormOfTeachingId)) {
      details = {
        ...programRequestDetail,
        formOfTeachingIds: newFormsOfTeaching,
        otherFormsOfTeaching: []
      };
    } else if (!programRequestDetail.formOfTeachingIds.includes(OtherFormOfTeachingId) &&
      newFormsOfTeaching.includes(OtherFormOfTeachingId)) {
      details = {
        ...programRequestDetail,
        formOfTeachingIds: newFormsOfTeaching,
        otherFormsOfTeaching: [""]
      };
    } else {
      details = {
        ...programRequestDetail,
        formOfTeachingIds: newFormsOfTeaching,
      };
    }
    doChange(event, details);
  };
  const handleOtherFormsOfTeachingChange = (event: any) => {
    const val = event.target.value as string[];
    const p: ProgramRequestDetail = {
      ...programRequestDetail,
      otherFormsOfTeaching: val,
    };
    doChange(event, p);
  };
  const handleKnowledgeCheckChange = (event: any) => {
    const newKnowledgeChecks = event.target.value as number[];
    let details: ProgramRequestDetail;
    if (programRequestDetail.knowledgeChecksIds.includes(OtherKnowledgeCheckId) &&
      !newKnowledgeChecks.includes(OtherKnowledgeCheckId)) {
      details = {
        ...programRequestDetail,
        knowledgeChecksIds: newKnowledgeChecks,
        otherKnowledgeChecks: []
      };
    } else if (!programRequestDetail.knowledgeChecksIds.includes(OtherKnowledgeCheckId) &&
      newKnowledgeChecks.includes(OtherKnowledgeCheckId)) {
      details = {
        ...programRequestDetail,
        knowledgeChecksIds: newKnowledgeChecks,
        otherKnowledgeChecks: [""]
      };
    } else {
      details = {
        ...programRequestDetail,
        knowledgeChecksIds: newKnowledgeChecks,
      };
    }
    doChange(event, details);
  };
  const handleOtherKnowledgeChecksChange = (event: any) => {
    const val = event.target.value as string[];
    const p: ProgramRequestDetail = {
      ...programRequestDetail,
      otherKnowledgeChecks: val,
    };
    doChange(event, p);
  };
  const handleEnrollmentConditionsChange = (event: any) => {
    var newEnrollmentConditions = event.target.value as number[];
    var details = programRequestDetail;
    if (programRequestDetail.enrollmentConditionsIds.includes(OtherEnrollmentConditionId) &&
      !newEnrollmentConditions.includes(OtherEnrollmentConditionId)) {
      details = {
        ...programRequestDetail,
        enrollmentConditionsIds: newEnrollmentConditions,
        otherEnrollmentConditions: []
      };
    } else if (!programRequestDetail.enrollmentConditionsIds.includes(OtherEnrollmentConditionId) &&
      newEnrollmentConditions.includes(OtherEnrollmentConditionId)) {
      details = {
        ...programRequestDetail,
        enrollmentConditionsIds: newEnrollmentConditions,
        otherEnrollmentConditions: [""]
      };
    } else {
      details = {
        ...programRequestDetail,
        enrollmentConditionsIds: newEnrollmentConditions,
      };
    }
    doChange(event, details);
  }
  const handleOtherEnrollmentConditionsChange = (event: any) => {
    const val = event.target.value as string[];
    const p: ProgramRequestDetail = {
      ...programRequestDetail,
      otherEnrollmentConditions: val,
    };
    doChange(event, p);
  };

  const doChange = (event: any, detail?: ProgramRequestDetail) => {
    if (!detail)
      onChange(event);
    else onProgramRequestDetailChange(detail);
    // onChange({
    //     ...event,
    //     target: {
    //         ...event.target,
    //         name: name,
    //         value: detail,
    //     }
    // });
  };

  return (
    <>
      <Row>
        <Col md={12} className={styles.rowTitleSpacing}>
          <Typography variant="smallHeading" component="h3">
            {t("request.programBasicInfo")}
          </Typography>
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={4}>
          {(programRequest.id > 0) &&
            <StaticField
              label={t("common.programType")}
              value={programRequest.programTypeName}
              className={styles.changedField}
            />
          }
          {(programRequest.id === 0) &&
            <Select
              label={t("request.programType")}
              name={nameof<typeof programRequest>("programTypeId")}
              value={programRequest.programTypeId}
              onChange={handleProgramTypeChange}
              errorText={getErrorForPath(`${name}.programType`)}
              selectData={codeBookToSelectData(programTypes)}
            />
          }
        </Col>
      </Row>
      <Row className={styles.dataRow}>
        <Col md={3}>
          <StaticField
            label={t("common.programDuration")}
            value={
              (Number(programRequestDetail.guidedLearningDuration) || 0) +
              (Number(programRequestDetail.independentLearningDuration) || 0) +
              (Number(programRequestDetail.workLearningDuration) || 0)
            }
            newValue={getOriginalLearningTotal()}
            className={styles.changedField}
          />
        </Col>
        {programRequestDetail.modules && programRequestDetail.modules.length > 0 &&
          <Col md={3}>
            <StaticField
              label={t("request.CSVETTotal")}
              value={programRequestDetail.modules.reduce((a, v) => a = a + v.learningSets.reduce((a, v) => a = a + (isNaN(v.csvet) ? 0 : v.csvet), 0), 0)}
              newValue={getOriginalCSVETTotal()}
              className={styles.changedField}
            />
          </Col>
        }
      </Row>
      {/* Durations row */}
      {programRequest.programTypeId !== ProgramType.BasicArtsPrograms
        && programRequest.programTypeId !== ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears
        && programRequest.programTypeId !== ProgramType.CroatianLanguageForForeigners &&
        <Row className={styles.dataRow}>
          <Col md={4}>
            {readonly &&
              <StaticField
                label={t("request.guidedLearningDuration")}
                value={programRequestDetail.guidedLearningDuration}
                newValue={programRequestOriginal?.guidedLearningDuration}
                className={styles.changedField}
              />
            }
            {!readonly &&
              <TextField
                label={t("request.guidedLearningDuration")}
                name={nameof<typeof programRequestDetail>("guidedLearningDuration")}
                type="number"
                value={programRequestDetail.guidedLearningDuration}
                errorText={errors?.guidedLearningDuration}
                onChange={handleGuidedLearningDuration}
                maxLength={5}
                min={0}
              />
            }
          </Col>
          <Col md={4}>
            {readonly &&
              <StaticField
                label={t("request.workLearningDuration")}
                value={programRequestDetail.workLearningDuration}
                newValue={programRequestOriginal?.workLearningDuration}
                className={styles.changedField}
              />
            }
            {!readonly &&
              <TextField
                label={t("request.workLearningDuration")}
                name={nameof<typeof programRequestDetail>("workLearningDuration")}
                type="number"
                value={programRequestDetail.workLearningDuration}
                errorText={errors?.workLearningDuration}
                onChange={handleWorkLearningDuration}
                maxLength={5}
                min={0}
              />
            }
          </Col>
          <Col md={4}>
            {readonly &&
              <StaticField
                label={t("request.independentLearningDuration")}
                value={programRequestDetail.independentLearningDuration}
                newValue={programRequestOriginal?.independentLearningDuration}
                className={styles.changedField}
              />
            }
            {!readonly &&
              <TextField
                label={t("request.independentLearningDuration")}
                name={nameof<typeof programRequestDetail>("independentLearningDuration")}
                type="number"
                value={programRequestDetail.independentLearningDuration}
                errorText={errors?.independentLearningDuration}
                onChange={handleIndependentLearningDuration}
                maxLength={5}
                min={0}
              />
            }
          </Col>
          {/* <Col md={3}>
                  <StaticField
                    label={t("common.programDuration")}
                    value={
                      programRequestDetail.guidedLearningDuration +
                      programRequestDetail.independentLearningDuration +
                      programRequestDetail.workLearningDuration
                    }
                    newValue={getOriginalLearningTotal()}
                    className={styles.changedField}
                  />
                </Col> */}
        </Row>
      }
      {/* ProgramName row */}
      <Row className={styles.dataRow}>
        <Col md={8}>
          {(programRequestDetail.id > 0) &&
            <StaticField
              label={t("common.programName")}
              value={programRequestDetail.programName}
              newValue={programRequestOriginal?.programName!}
              className={styles.changedField}
            />
          }
          {(programRequestDetail.id === 0) &&
            <Autocomplete
              label={t("common.programName")}
              name={nameof<typeof programRequestDetail>("programId")}
              value={programRequestDetail.programId}
              onChange={handleProgramChange}
              selectData={getProgramsSelectData()}
              errorText={errors?.programId}
              infoText={t("request.programNameInfoMessage")}
            />
          }
        </Col>
        <Col md={4}>
          <StaticField
            label={t("common.programCode")}
            value={programRequestDetail.programCode}
            newValue={programRequestOriginal?.programCode!}
            className={styles.changedField}
          />
        </Col>
      </Row>
      {programTypes && programs && (
        <Row className={styles.dataRow}>
          <Col md={12}>
            <ConditionalBasicInfo
              programTypeId={programRequest.programTypeId}
              programRequestDetail={programRequestDetail}
              programRequestDetailChange={programRequestOriginal}
              programData={programs.find(pr => pr.id === programRequestDetail.programId)}
              readonly={readonly}
              onChange={doChange}
              errors={errors}
            />
          </Col>
        </Row>
      )}
      <Row className={styles.dataRow}>
        <Col md={4}>
          {readonly &&
            (programRequestOriginal
              ? (
                <StringListDiffField
                  label={t("request.formOfTeaching")}
                  original={programRequestDetail.formOfTeaching ?? []}
                  corrected={programRequestOriginal.formOfTeaching ?? []}
                  deletedClassName={styles.deletedField}
                  addedClassName={styles.changedField}
                />
              ) : (
                <StaticField
                  label={t("request.formOfTeaching")}
                  value={programRequestDetail.formOfTeaching?.map(ft => ft).join(", ") ?? ""}
                />
              ))
          }
          {!readonly &&
            <Select
              multiple
              label={t("request.formOfTeaching")}
              name={nameof<typeof programRequestDetail>("formOfTeachingIds")}
              value={programRequestDetail.formOfTeachingIds}
              onChange={handleFormsOfTeachingChange}
              selectData={codeBookToSelectData(formsOfTeaching)}
              errorText={errors?.formOfTeachingIds?.toString()}
            />
          }
        </Col>
        <Col md={4}>
          {readonly &&
            (programRequestOriginal
              ? (
                <StringListDiffField
                  label={t("request.knowledgeChecks")}
                  original={programRequestDetail.knowledgeChecks ?? []}
                  corrected={programRequestOriginal.knowledgeChecks ?? []}
                  deletedClassName={styles.deletedField}
                  addedClassName={styles.changedField}
                />
              ) : (
                <StaticField
                  label={t("request.knowledgeChecks")}
                  value={programRequestDetail.knowledgeChecks?.map(kc => kc).join(", ") ?? ""}
                />
              ))
          }
          {!readonly &&
            <Select
              multiple
              label={t("request.knowledgeChecks")}
              name={nameof<typeof programRequestDetail>("knowledgeChecksIds")}
              value={programRequestDetail.knowledgeChecksIds}
              onChange={handleKnowledgeCheckChange}
              selectData={codeBookToSelectData(knowledgeChecks)}
              errorText={errors?.knowledgeChecksIds?.toString()}
            />
          }
        </Col>
        <Col md={4}>
          {readonly &&
            (programRequestOriginal
              ? (
                <StringListDiffField
                  label={t("request.enrollmentConditions")}
                  original={programRequestDetail.enrollmentConditions ?? []}
                  corrected={programRequestOriginal.enrollmentConditions ?? []}
                  deletedClassName={styles.deletedField}
                  addedClassName={styles.changedField}
                />
              ) : (
                <StaticField
                  label={t("request.enrollmentConditions")}
                  value={programRequestDetail.enrollmentConditions?.map(ec => ec).join(", ") ?? ""}
                />
              ))
          }
          {!readonly &&
            <Select
              multiple
              label={t("request.enrollmentConditions")}
              name={nameof<typeof programRequestDetail>("enrollmentConditionsIds")}
              value={programRequestDetail.enrollmentConditionsIds}
              onChange={handleEnrollmentConditionsChange}
              selectData={codeBookToSelectData(enrollmentConditions)}
              errorText={errors?.enrollmentConditionsIds?.toString()}
            />
          }
        </Col>
      </Row>
      {(!!programRequestDetail.otherFormsOfTeaching?.length
        || !!programRequestOriginal?.otherFormsOfTeaching?.length) &&

        <Row className={styles.dataRow}>
          <Col md={12}>
            {readonly &&
              (programRequestOriginal
                ? (
                  <StringListDiffField
                    label={t("request.otherFormsOfTeaching")}
                    original={programRequestDetail.otherFormsOfTeaching ?? []}
                    corrected={
                      programRequestOriginal.formOfTeachingIds?.includes(OtherFormOfTeachingId)
                        ? (programRequestOriginal.otherFormsOfTeaching ?? [])
                        : []
                    }
                    deletedClassName={styles.deletedField}
                    addedClassName={styles.changedField}
                  />
                ) : (
                  <StaticField
                    label={t("request.otherFormsOfTeaching")}
                    value={programRequestDetail.otherFormsOfTeaching?.map(oft => oft).join(", ") ?? ""}
                  />
                ))
            }
            {!readonly && programRequestDetail.formOfTeachingIds.includes(OtherFormOfTeachingId) &&
              <OtherData
                name={`${nameof<typeof programRequestDetail>("otherFormsOfTeaching")}`}
                header={t("request.formOfTeaching")}
                label={t("request.otherFormsOfTeaching")}
                addButtonLabel={t("request.addNewFormOfTeaching")}
                values={programRequestDetail.otherFormsOfTeaching}
                onChange={handleOtherFormsOfTeachingChange}
                getErrorForPath={getErrorForPath}
              />
            }
          </Col>
        </Row>
      }
      {(!!programRequestDetail.otherKnowledgeChecks?.length
        || !!programRequestOriginal?.otherKnowledgeChecks?.length) &&
        <Row className={styles.dataRow}>
          <Col md={12}>
            {readonly &&
              (programRequestOriginal
                ? (
                  <StringListDiffField
                    label={t("request.otherKnowledgeChecks")}
                    original={programRequestDetail.otherKnowledgeChecks ?? []}
                    corrected={
                      programRequestOriginal.knowledgeChecksIds?.includes(OtherKnowledgeCheckId)
                        ? (programRequestOriginal.otherKnowledgeChecks ?? [])
                        : []
                    }
                    deletedClassName={styles.deletedField}
                    addedClassName={styles.changedField}
                  />
                ) : (
                  <StaticField
                    label={t("request.otherKnowledgeChecks")}
                    value={programRequestDetail.otherKnowledgeChecks?.map(okc => okc).join(", ") ?? ""}
                  />
                ))
            }
            {!readonly && programRequestDetail.knowledgeChecksIds.includes(OtherKnowledgeCheckId) &&
              <OtherData
                name={`${nameof<typeof programRequestDetail>("otherKnowledgeChecks")}`}
                header={t("request.knowledgeChecks")}
                label={t("request.otherKnowledgeChecks")}
                addButtonLabel={t("request.addNewKnowledgeCheck")}
                values={programRequestDetail.otherKnowledgeChecks}
                onChange={handleOtherKnowledgeChecksChange}
                getErrorForPath={getErrorForPath}
              />
            }
          </Col>
        </Row>
      }
      {(!!programRequestDetail.otherEnrollmentConditions?.length
        || !!programRequestOriginal?.otherEnrollmentConditions?.length) &&
        <Row className={styles.dataRow}>
          <Col md={12}>
            {readonly &&
              (programRequestOriginal
                ? (
                  <StringListDiffField
                    label={t("request.otherEnrollmentConditions")}
                    original={programRequestDetail.otherEnrollmentConditions ?? []}
                    corrected={
                      programRequestOriginal.enrollmentConditionsIds?.includes(OtherEnrollmentConditionId)
                        ? (programRequestOriginal.otherEnrollmentConditions ?? [])
                        : []
                    }
                    deletedClassName={styles.deletedField}
                    addedClassName={styles.changedField}
                  />
                ) : (
                  <StaticField
                    label={t("request.otherEnrollmentConditions")}
                    value={programRequestDetail.otherEnrollmentConditions?.map(oec => oec).join(", ") ?? ""}
                  />
                ))
            }
            {!readonly && programRequestDetail.enrollmentConditionsIds.includes(OtherEnrollmentConditionId) &&
              <OtherEnrollmentConditionsInfo
                name={`${nameof<typeof programRequestDetail>("otherEnrollmentConditions")}`}
                getErrorForPath={getErrorForPath}
                otherEnrollmentConditions={programRequestDetail.otherEnrollmentConditions}
                onChange={handleOtherEnrollmentConditionsChange}
              />
            }
          </Col>
        </Row>
      }
      {readonly && programRequestDetail.competences.length > 0 &&
        <>
          <Row>
            <Col md={12} className={styles.rowTitleSpacing}>
              <Typography variant="smallHeading" component="h3">
                {t("request.competences")}
              </Typography>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Table {...tableProps} />
            </Col>
          </Row>
        </>
      }
      {!readonly && programRequest.programTypeId !== ProgramType.BasicArtsPrograms
        && programRequest.programTypeId !== ProgramType.MiddleSchoolArtsProgramsLongerThanThreeYears
        && programRequest.programTypeId !== ProgramType.CroatianLanguageForForeigners &&
        <CompetenceInfo
          name={`${nameof<typeof programRequestDetail>("competences")}`}
          getErrorForPath={getErrorForPath}
          competences={programRequestDetail.competences}
          onChange={doChange}
        />
      }
    </>
  );
};